import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { isEventStatus } from '@barso/shared';
import { bazaarApi, helperApi } from '../api/endpoints';
import type { NavEvent } from '../components/navModel';
import { loadOrganizations } from './useOrganizations';

export interface NavRoles {
  events: NavEvent[] | null;
  registrationCount: number | null;
  managedOrgCount: number | null;
}

const UNKNOWN: NavRoles = { events: null, registrationCount: null, managedOrgCount: null };

/** Mindestabstand zwischen zwei Aktualisierungen beim Seitenwechsel. */
const REFRESH_MS = 60_000;

/** Anmeldungen, die noch „laufen“: nicht abgesagt, abgelehnt oder zu einem beendeten Basar gehörend. */
const ENDED = new Set(['closed', 'settled', 'archived']);

/**
 * Rollen der Person für die untere Leiste der App (U13): Basar-Rollen (`/me/helper-events`), eigene Anmeldungen
 * (`/me/registrations`) und Organisationen mit Inhaber- oder Verwaltungsrolle (`/orgs`). Jede Quelle für sich: schlägt
 * eine fehl, bleibt ihr Wert `null` (unbekannt) und die Leiste fällt auf den sicheren Fall zurück. Der Zustand gehört
 * zu einer Person (`userKey`): bei Wechsel gilt sofort „unbekannt“. Neu geladen wird beim Seitenwechsel, höchstens
 * einmal je Minute. Nur wenn `enabled` (nativ und angemeldet).
 */
export function useNavRoles(enabled: boolean, userKey: string | null): NavRoles {
  const [state, setState] = useState<{ key: string | null; roles: NavRoles }>({
    key: userKey,
    roles: UNKNOWN,
  });
  const { pathname } = useLocation();
  const loadedAt = useRef<{ key: string | null; at: number } | null>(null);
  const current = useRef(userKey);
  useEffect(() => {
    current.current = userKey;
  }, [userKey]);
  // Beim Aktivieren, bei Nutzerwechsel und beim Seitenwechsel (höchstens einmal je Minute) laden. Eine laufende
  // Abfrage wird bei Seitenwechsel nicht verworfen, nur bei Nutzerwechsel (Prüfung über `current`).
  useEffect(() => {
    if (!enabled) return;
    const last = loadedAt.current;
    if (last?.key === userKey && Date.now() - last.at < REFRESH_MS) return;
    loadedAt.current = { key: userKey, at: Date.now() };
    const merge = (part: Partial<NavRoles>) => {
      if (current.current !== userKey) return;
      setState((old) => ({
        key: userKey,
        roles: { ...(old.key === userKey ? old.roles : UNKNOWN), ...part },
      }));
    };
    helperApi.myEvents().then(
      (list) => {
        merge({
          events: list.flatMap((event) =>
            isEventStatus(event.status)
              ? [
                  {
                    eventId: event.eventId,
                    title: event.title,
                    status: event.status,
                    roles: event.roles,
                  },
                ]
              : [],
          ),
        });
      },
      () => undefined,
    );
    bazaarApi.myRegistrations().then(
      (list) => {
        merge({
          registrationCount: list.filter(
            (entry) =>
              entry.status !== 'cancelled' &&
              entry.status !== 'rejected' &&
              !ENDED.has(entry.eventStatus),
          ).length,
        });
      },
      () => undefined,
    );
    loadOrganizations().then(
      (list) => {
        merge({
          managedOrgCount: list.filter((org) => org.role === 'owner' || org.role === 'admin')
            .length,
        });
      },
      () => undefined,
    );
  }, [enabled, userKey, pathname]);
  return enabled && state.key === userKey ? state.roles : UNKNOWN;
}
